import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { useAuthStore, useCartStore } from '../lib/store';
import { formatPrice } from '../lib/utils';

export const Navbar = ({ onAuthClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuthStore();
  const { items, getItemCount, getTotal, removeItem, updateQuantity } = useCartStore();
  const navigate = useNavigate();
  const itemCount = getItemCount();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_fire-cider-shop/artifacts/007pzxhk_IMG_1923.jpeg" 
              alt="East Burford Ltd" 
              className="h-16 w-auto transition-transform group-hover:scale-105"
              style={{ mixBlendMode: 'multiply' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-home">Home</Link>
            <Link to="/products" className="text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-products">Shop</Link>
            <Link to="/serving-suggestions" className="text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-serving">Serving</Link>
            <Link to="/mocktails" className="text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-mocktails">Mocktails</Link>
            {isAuthenticated && user?.is_admin && (
              <Link to="/admin" className="text-secondary hover:text-primary transition-colors font-medium" data-testid="nav-admin">Admin</Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                      {itemCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle className="font-heading text-2xl">Your Cart</SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col h-[calc(100vh-200px)]">
                  {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                      <ShoppingCart className="w-16 h-16 mb-4 opacity-50" />
                      <p>Your cart is empty</p>
                      {!isAuthenticated && (
                        <p className="text-sm mt-2">Sign in to add items</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 overflow-y-auto space-y-4">
                        {items.map((item) => (
                          <div key={item.id} className="flex gap-4 p-4 bg-surface rounded-lg" data-testid={`cart-item-${item.id}`}>
                            <img src={item.product_image} alt={item.product_name} className="w-20 h-20 object-cover rounded-md" />
                            <div className="flex-1">
                              <h4 className="font-medium text-secondary">{item.product_name}</h4>
                              <p className="text-primary font-semibold">{formatPrice(item.product_price)}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))} data-testid={`decrease-qty-${item.id}`}>-</Button>
                                <span className="w-8 text-center">{item.quantity}</span>
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, item.quantity + 1)} data-testid={`increase-qty-${item.id}`}>+</Button>
                                <Button variant="ghost" size="sm" className="ml-auto text-destructive" onClick={() => removeItem(item.id)} data-testid={`remove-item-${item.id}`}>Remove</Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t pt-4 mt-4">
                        <div className="flex justify-between text-lg font-semibold mb-4">
                          <span>Total</span>
                          <span className="text-primary">{formatPrice(getTotal())}</span>
                        </div>
                        <Button className="w-full" size="lg" onClick={() => navigate('/checkout')} data-testid="checkout-button">
                          Checkout
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Auth */}
            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/account" data-testid="account-link">
                  <Button variant="ghost" size="icon">
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
                  Logout
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={onAuthClick} className="hidden md:flex" data-testid="login-button">
                Sign In
              </Button>
            )}

            {/* Mobile menu */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu content */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t animate-fade-in-up">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/products" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
              <Link to="/serving-suggestions" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Serving Suggestions</Link>
              <Link to="/mocktails" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Mocktails</Link>
              {isAuthenticated && user?.is_admin && (
                <Link to="/admin" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
              )}
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="text-secondary hover:text-primary transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Account</Link>
                  <Button variant="outline" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>Logout</Button>
                </>
              ) : (
                <Button onClick={() => { onAuthClick(); setMobileMenuOpen(false); }}>Sign In</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
